import { memo, useContext, useEffect, useMemo, useState } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { useQuery } from "@tanstack/react-query";
import { extractInternalLinks, parseInternalHref } from "@brainlog/shared";
import { get } from "@/lib/api";
import { openNote, openPaper, openView } from "@/lib/store";
import type { ResolvedRef } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Paperclip } from "lucide-react";
import { downloadFile } from "@/features/files/download";
import { figureImageUrl } from "@/features/files/figureImages";
import { canPreview, FilePreviewDialog } from "@/features/files/FilePreviewDialog";
import { LinkOpenerContext, PANEL_LINK_TYPES } from "./linkOpener";

function fileFromRef(id: string, r: ResolvedRef) {
  return { id, filename: r.title ?? "file", mime: r.mime ?? "application/octet-stream", sizeBytes: r.sizeBytes ?? 0, sha256: r.sha256 ?? "", encrypted: !!r.encrypted };
}

/**
 * Sanitizer schema: GitHub defaults + math classes + our callout class + figures (an image of a
 * project file, `bl://file/…`, shown from its attachment). Scripts/HTML never render.
 */
const schema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), "figure", "figcaption"],
  protocols: { ...defaultSchema.protocols, href: [...(defaultSchema.protocols?.href ?? []), "bl"], src: [...(defaultSchema.protocols?.src ?? []), "bl"] },
  attributes: {
    ...defaultSchema.attributes,
    figure: [["className", "md-figure"]],
    code: [...(defaultSchema.attributes?.code ?? []), ["className", /^language-./, "math-inline", "math-display"]],
    span: [...(defaultSchema.attributes?.span ?? []), ["className", "callout-label"]],
    blockquote: [...(defaultSchema.attributes?.blockquote ?? []), ["className", "callout-ai"]],
    div: [...(defaultSchema.attributes?.div ?? []), ["className", "math", "math-display"]],
  },
};

/** remark plugin: `> [!AI-ASSISTED] …` blockquotes become labelled AI-assisted callouts. */
function remarkAiCallout() {
  return (tree: any) => {
    const walk = (node: any) => {
      if (node.type === "blockquote") {
        const p = node.children?.[0];
        const first = p?.type === "paragraph" ? p.children?.[0] : null;
        if (first?.type === "text" && /^\[!AI-ASSISTED\]/i.test(first.value)) {
          first.value = first.value.replace(/^\[!AI-ASSISTED\]\s*/i, "");
          node.data = { ...(node.data ?? {}), hProperties: { className: ["callout-ai"] } };
          p.children.unshift({ type: "html", value: "" });
          p.children.unshift({ type: "strong", data: { hName: "span", hProperties: { className: ["callout-label"] } }, children: [{ type: "text", value: "AI-assisted text — accepted by a user" }] });
        }
      }
      node.children?.forEach(walk);
    };
    walk(tree);
  };
}

/**
 * remark plugin: a paragraph that is one image of a project file (`![Caption](bl://file/…)`) is a
 * figure: the image, then "Figure n. Caption" below; numbers start at `start` (a section's first).
 */
function remarkFigures(options: { start?: number } = {}) {
  return (tree: any) => {
    let n = options.start ?? 1;
    for (const p of tree.children ?? []) {
      if (p.type !== "paragraph") continue;
      const kids = p.children.filter((c: any) => !(c.type === "text" && !c.value.trim()));
      const img = kids.length === 1 && kids[0].type === "image" && /^bl:\/\/file\//i.test(kids[0].url) ? kids[0] : null;
      if (!img) continue;
      const caption = String(img.alt ?? "").trim();
      p.data = { hName: "figure", hProperties: { className: ["md-figure"] } };
      p.children = [img, { type: "paragraph", data: { hName: "figcaption" }, children: [{ type: "strong", children: [{ type: "text", value: `Figure ${n++}.` }] }, { type: "text", value: caption ? ` ${caption}` : "" }] }];
    }
  };
}

/** A figure's image: loaded (or decrypted) in the browser; a plain message when it cannot be shown. */
function FigureImage({ id, alt }: { id: string; alt: string }) {
  const [state, setState] = useState<{ url?: string; error?: string }>({});
  useEffect(() => {
    let alive = true;
    figureImageUrl(id).then(
      (url) => alive && setState({ url }),
      (err: Error) => alive && setState({ error: err.message }),
    );
    return () => {
      alive = false;
    };
  }, [id]);
  if (state.url) return <img src={state.url} alt={alt} />;
  return <span className="md-figure-status">{state.error ?? "Loading the image…"}</span>;
}

function urlTransform(url: string) {
  if (url.startsWith("bl://")) return parseInternalHref(url) ? url : "";
  return defaultUrlTransform(url);
}

export function useResolvedLinks(markdown: string) {
  const refs = useMemo(() => {
    const links = extractInternalLinks(markdown);
    return [...new Set(links.map((l) => `${l.type}:${l.id}`))].sort();
  }, [markdown]);
  return useQuery({
    queryKey: ["resolve", refs.join(",")],
    queryFn: async () => (await get<{ resolved: Record<string, ResolvedRef> }>(`/api/resolve?refs=${encodeURIComponent(refs.join(","))}`)).resolved,
    enabled: refs.length > 0,
    staleTime: 10_000,
  });
}

export function InternalLink({ href, children, resolved }: { href: string; children: React.ReactNode; resolved?: Record<string, ResolvedRef> }) {
  const [previewing, setPreviewing] = useState(false);
  const opener = useContext(LinkOpenerContext);
  const link = parseInternalHref(href);
  if (!link) return <span>{children}</span>;
  const r = resolved?.[`${link.type}:${link.id}`];
  if (resolved && (!r || !r.accessible)) {
    return (
      <span className="rounded bg-muted px-1 text-muted-foreground line-through decoration-1" title="Unavailable: this item does not exist or you do not have access to it.">
        {children}
        <span className="sr-only"> (unavailable link)</span>
      </span>
    );
  }
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Inside a note view, the item opens beside the note.
    if (opener && PANEL_LINK_TYPES.has(link.type)) {
      const block = (e.currentTarget as HTMLElement).closest("li, blockquote, p, td, th");
      return opener(href, block?.textContent ?? "");
    }
    const title = r?.title ?? "Item";
    if (link.type === "note") openNote(link.id, title, r?.workspaceId);
    else if (link.type === "paper" || link.type === "cite") openPaper(r?.redirectedTo ?? link.id, title, link.page, r?.workspaceId);
    else if (link.type === "task") openView("backlog", r?.workspaceId, "Backlog", { task: link.id });
    else if (link.type === "manuscript") openView("manuscript", r?.workspaceId, title, undefined, link.id);
    else if (link.type === "file" && r) {
      const file = fileFromRef(link.id, r);
      if (canPreview(file)) setPreviewing(true);
      else void downloadFile(file);
    }
  };
  const isCite = link.type === "cite";
  const isFile = link.type === "file";
  const tip = r
    ? `${r.deleted ? "[in trash] " : ""}${r.title ?? ""}${link.page ? ` — PDF page ${link.page}${link.pageLabel ? ` (printed ${link.pageLabel})` : ""}` : ""}${r.redirectedTo ? " (merged into another library record)" : ""}`
    : "Resolving…";
  return (
    <>
      <a
        href={href}
        onClick={onClick}
        title={tip}
        className={cn(isCite && "rounded bg-primary/10 px-1 no-underline", link.type === "task" && "rounded bg-accent px-1 no-underline", isFile && "rounded bg-muted px-1 no-underline")}
        data-internal-link={link.type}
      >
        {isFile && <Paperclip className="mr-0.5 inline size-3 align-[-1px]" aria-hidden />}
        {children}
      </a>
      {isFile && previewing && r && <FilePreviewDialog file={fileFromRef(link.id, r)} onClose={() => setPreviewing(false)} />}
    </>
  );
}

export const MarkdownPreview = memo(function MarkdownPreview({ markdown, className, figureStart }: { markdown: string; className?: string; /** The first figure's number (a manuscript section after others). */ figureStart?: number }) {
  const resolved = useResolvedLinks(markdown);
  return (
    <div className={cn("md-preview", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath, remarkAiCallout, [remarkFigures, { start: figureStart }]]}
        rehypePlugins={[[rehypeSanitize, schema], [rehypeKatex, { throwOnError: false, strict: "ignore", trust: false }]]}
        urlTransform={urlTransform}
        components={{
          a: ({ href, children }) =>
            href?.startsWith("bl://") ? (
              <InternalLink href={href} resolved={resolved.data ?? (resolved.isLoading ? undefined : {})}>
                {children}
              </InternalLink>
            ) : (
              <a href={href} target="_blank" rel="noopener noreferrer nofollow">
                {children}
              </a>
            ),
          input: (props) => <input {...props} disabled aria-label="Checkbox (note text, not a task)" />,
          // Images of project files are shown from their attachment; other internal links are not images.
          img: ({ src, alt }) => {
            const link = typeof src === "string" && src.startsWith("bl://") ? parseInternalHref(src) : null;
            if (link?.type === "file") return <FigureImage id={link.id} alt={alt ?? ""} />;
            if (typeof src === "string" && src.startsWith("bl://")) return <span>{alt}</span>;
            return <img src={src} alt={alt ?? ""} />;
          },
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
});
